import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const SESSION_STORAGE_KEYS = {
  SHOWN: 'landingFeedback_shown',
  CTA_CLICKED: 'landingFeedback_ctaClicked',
  START_TIME: 'landingFeedback_startTime',
};

const TRIGGER_THRESHOLDS = {
  MIN_TIME_SECONDS: 60,
  MIN_SCROLL_PERCENT: 30,
  HOVER_COUNT_TO_TRIGGER: 3,
};

/**
 * Hook to track user behavior and determine when to show feedback prompt
 * 
 * Primary trigger (ALL must be true):
 * - Time on page ≥ 60 seconds
 * - Scroll depth ≥ 50%
 * - No primary CTA clicked
 * - Not already shown this session
 * 
 * Secondary trigger (exit intent):
 * - Mouse leaves viewport top edge (desktop only)
 * - Still requires: ≥ 60 seconds AND no CTA clicked
 */
const useFeedbackTrigger = () => {
  const [searchParams] = useSearchParams();
  const [shouldShow, setShouldShow] = useState(false);
  const [triggerReason, setTriggerReason] = useState(null);
  const [ctaClicked, setCtaClicked] = useState(false);

  // For testing: ?feedbackTest=1 shows feedback on page load
  const isTestMode = searchParams.get('feedbackTest') === '1';

  const startTimeRef = useRef(null);
  const scrollPercentRef = useRef(0);
  const timeOnPageRef = useRef(0);
  const hasShownRef = useRef(false);
  const exitIntentDetectedRef = useRef(false);
  const isDesktopRef = useRef(false);
  const hoverCountRef = useRef(0);

  // Trigger the feedback prompt (defined first - used by init effect)
  const triggerFeedback = useCallback((reason) => {
    if (hasShownRef.current) return;

    hasShownRef.current = true;
    sessionStorage.setItem(SESSION_STORAGE_KEYS.SHOWN, 'true');
    setTriggerReason(reason);
    setShouldShow(true);
  }, []);

  // Initialize session storage tracking
  useEffect(() => {
    // Test mode: show on page load (ignore session storage)
    if (isTestMode) {
      const timer = setTimeout(() => {
        if (!hasShownRef.current) {
          triggerFeedback('test');
        }
      }, 300);
      return () => clearTimeout(timer);
    }

    // Check if already shown this session
    const alreadyShown = sessionStorage.getItem(SESSION_STORAGE_KEYS.SHOWN) === 'true';
    if (alreadyShown) {
      hasShownRef.current = true;
      return;
    }

    // Check if CTA was already clicked
    const ctaWasClicked = sessionStorage.getItem(SESSION_STORAGE_KEYS.CTA_CLICKED) === 'true';
    if (ctaWasClicked) {
      setCtaClicked(true);
      return;
    }

    // Get or set start time
    const storedStartTime = sessionStorage.getItem(SESSION_STORAGE_KEYS.START_TIME);
    if (storedStartTime) {
      startTimeRef.current = parseInt(storedStartTime, 10);
    } else {
      startTimeRef.current = Date.now();
      sessionStorage.setItem(SESSION_STORAGE_KEYS.START_TIME, startTimeRef.current.toString());
    }

    // Detect desktop (for exit intent)
    isDesktopRef.current = window.innerWidth >= 768; // Tablet and up
  }, [triggerFeedback, isTestMode]);

  // Check if all trigger conditions are met
  const checkTriggerConditions = useCallback((reason = 'behavior') => {
    // Double-check sessionStorage to prevent race conditions
    const ctaWasClicked = sessionStorage.getItem(SESSION_STORAGE_KEYS.CTA_CLICKED) === 'true';
    const alreadyShown = sessionStorage.getItem(SESSION_STORAGE_KEYS.SHOWN) === 'true';
    
    if (hasShownRef.current || ctaClicked || ctaWasClicked || alreadyShown) {
      return;
    }

    const timeMet = timeOnPageRef.current >= TRIGGER_THRESHOLDS.MIN_TIME_SECONDS;
    const scrollMet = scrollPercentRef.current >= TRIGGER_THRESHOLDS.MIN_SCROLL_PERCENT;
    const noCtaClicked = !ctaClicked && !ctaWasClicked;
    const hoverMet = hoverCountRef.current >= TRIGGER_THRESHOLDS.HOVER_COUNT_TO_TRIGGER;

    // Debug logging (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('[Feedback Trigger]', {
        reason,
        timeMet,
        timeOnPage: Math.round(timeOnPageRef.current),
        scrollMet,
        scrollPercent: Math.round(scrollPercentRef.current),
        hoverMet,
        hoverCount: hoverCountRef.current,
        noCtaClicked,
        hasShown: hasShownRef.current,
        ctaClicked,
      });
    }

    // For exit intent, still need time requirement
    if (reason === 'exit_intent') {
      if (timeMet && noCtaClicked) {
        triggerFeedback('exit_intent');
      }
    } else if (reason === 'hover') {
      // Hover trigger: 3rd hover triggers feedback
      if (hoverMet && noCtaClicked) {
        triggerFeedback('hover');
      }
    } else {
      // Primary trigger: all conditions must be met (time + scroll)
      if (timeMet && scrollMet && noCtaClicked) {
        triggerFeedback('behavior');
      }
    }
  }, [ctaClicked, triggerFeedback]);

  // Track time on page
  useEffect(() => {
    if (hasShownRef.current || ctaClicked || !startTimeRef.current) return;

    const interval = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      timeOnPageRef.current = elapsed;
      checkTriggerConditions();
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [ctaClicked, checkTriggerConditions]);

  // Track scroll depth
  useEffect(() => {
    if (hasShownRef.current || ctaClicked) return;

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollableHeight = documentHeight - windowHeight;
      
      if (scrollableHeight > 0) {
        scrollPercentRef.current = (scrollTop / scrollableHeight) * 100;
      } else {
        scrollPercentRef.current = 100; // If page fits in viewport, consider it 100% scrolled
      }
      
      checkTriggerConditions();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [ctaClicked, checkTriggerConditions]);

  // Exit intent detection (desktop only)
  useEffect(() => {
    if (hasShownRef.current || ctaClicked || !isDesktopRef.current) return;

    const handleMouseLeave = (e) => {
      // Only trigger if mouse is leaving through the top of the viewport
      if (e.clientY <= 0 && !exitIntentDetectedRef.current) {
        exitIntentDetectedRef.current = true;
        checkTriggerConditions('exit_intent');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [ctaClicked, checkTriggerConditions]);

  // Mark CTA as clicked
  const markCtaClicked = useCallback(() => {
    if (ctaClicked) return;
    
    setCtaClicked(true);
    sessionStorage.setItem(SESSION_STORAGE_KEYS.CTA_CLICKED, 'true');
    
    // Hide feedback if it was showing
    if (shouldShow) {
      setShouldShow(false);
    }
  }, [ctaClicked, shouldShow]);

  // Track CTA hover
  const markCtaHover = useCallback(() => {
    if (hasShownRef.current || ctaClicked) return;
    
    hoverCountRef.current += 1;
    
    // Check if hover count meets threshold
    if (hoverCountRef.current >= TRIGGER_THRESHOLDS.HOVER_COUNT_TO_TRIGGER) {
      checkTriggerConditions('hover');
    }
  }, [checkTriggerConditions]);

  // Dismiss feedback (user closed it)
  const dismissFeedback = useCallback(() => {
    setShouldShow(false);
    // Don't reset hasShownRef - we want it to stay dismissed for the session
  }, []);

  return {
    shouldShow,
    triggerReason,
    markCtaClicked,
    markCtaHover,
    dismissFeedback,
    isTestMode,
  };
};

export default useFeedbackTrigger;
